import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Chip, Tab, Tabs } from '@mui/material';
import { CheckCircle2 } from 'lucide-react';
import vendingApiClient from '../../Services/VendingApi';
import { COLORS } from '../../Styles/Colors';

/** Soft AIMS status colors — no MUI success green / warning orange fills */
export const mapStatusColor = (status: string): string => {
  switch (String(status || '').toLowerCase()) {
    case 'mapped':
      return COLORS.teal;
    case 'conflict':
      return COLORS.orange;
    case 'ignored':
    case 'unmapped':
    default:
      return COLORS.lightGray;
  }
};

export const mapStatusLabel = (status: string): string => {
  switch (String(status || '').toLowerCase()) {
    case 'mapped':
      return 'Mapped';
    case 'conflict':
      return 'Conflict';
    case 'ignored':
    default:
      return 'Unmapped';
  }
};

export const MapStatusChip = ({ status }: { status: string }) => {
  const color = mapStatusColor(status);
  return (
    <Chip
      label={mapStatusLabel(status)}
      size="small"
      variant="outlined"
      sx={{
        height: 24,
        fontWeight: 700,
        fontSize: '0.6875rem',
        color,
        backgroundColor: `${color}18`,
        border: `1px solid ${color}55`,
        '& .MuiChip-label': { px: 1 },
      }}
    />
  );
};

export const mappingCellText = {
  fontSize: '0.8125rem',
  lineHeight: 1.3,
  color: COLORS.darkGray,
  overflow: 'hidden' as const,
  textOverflow: 'ellipsis' as const,
  whiteSpace: 'nowrap' as const,
};

export const mappingActionLinkSx = {
  color: COLORS.teal,
  cursor: 'pointer',
  fontSize: '0.75rem',
  fontWeight: 600,
  border: 'none',
  background: 'none',
  padding: 0,
  lineHeight: 1,
  textTransform: 'none' as const,
  '&:hover': { textDecoration: 'underline' },
  '&:disabled': { color: COLORS.lightGray, cursor: 'default', textDecoration: 'none' },
};

export const mappingTabsSx = {
  mb: 2,
  minHeight: 40,
  borderBottom: 1,
  borderColor: 'divider',
  '& .MuiTab-root': {
    textTransform: 'none',
    fontWeight: 600,
    minHeight: 40,
    color: COLORS.lightGray,
  },
  '& .Mui-selected': { color: `${COLORS.teal} !important` },
  '& .MuiTabs-indicator': { backgroundColor: COLORS.teal, height: 3, borderRadius: '3px 3px 0 0' },
};

export const mappingToolbarSx = {
  px: { xs: 2, md: 3 },
  pt: 2,
  pb: 1,
  flexShrink: 0,
};

export const mappingEmptyStateSx = {
  mx: { xs: 2, md: 3 },
  textAlign: 'center' as const,
  py: 8,
  px: 2,
  borderRadius: 2,
  border: `1px dashed ${COLORS.lightGray}`,
  backgroundColor: '#fafbfc',
};

export type MappingType = 'machines' | 'products' | 'employees';

// Setup order: products and employees only make sense once machines are mapped.
const MAPPING_TYPES: { value: MappingType; label: string }[] = [
  { value: 'machines', label: 'Machines' },
  { value: 'products', label: 'Products' },
  { value: 'employees', label: 'Employees' },
];

/**
 * Rows still needing a decision per type (unmapped incl. ignored, plus
 * conflict) — same buckets the pages show. Read from the existing list
 * endpoints with limit=1: products/employees return meta.stats, machines only
 * meta.total, so it is asked for the unmapped filter directly.
 */
const loadPendingCounts = async (): Promise<Partial<Record<MappingType, number>>> => {
  const getJson = async (endpoint: string) => (await vendingApiClient(endpoint)).json();
  const [machines, products, employees] = await Promise.allSettled([
    getJson('/mappings/machines?location_map_status=unmapped&limit=1'),
    getJson('/mappings/products?limit=1'),
    getJson('/mappings/employees?limit=1'),
  ]);

  const counts: Partial<Record<MappingType, number>> = {};
  if (machines.status === 'fulfilled') {
    counts.machines = Number(machines.value?.meta?.total ?? 0);
  }
  if (products.status === 'fulfilled') {
    const stats = products.value?.meta?.stats ?? {};
    counts.products = Number(stats.unmapped ?? 0) + Number(stats.conflict ?? 0);
  }
  if (employees.status === 'fulfilled') {
    const stats = employees.value?.meta?.stats ?? {};
    counts.employees = Number(stats.unmapped ?? 0) + Number(stats.conflict ?? 0);
  }
  return counts;
};

const PendingBadge = ({ count }: { count?: number }) => {
  if (count == null) return null;
  if (count === 0) {
    return <CheckCircle2 size={16} color={COLORS.teal} aria-label="All mapped" />;
  }
  return (
    <Box
      component="span"
      sx={{
        minWidth: 20,
        height: 20,
        px: 0.75,
        borderRadius: 10,
        fontSize: '0.6875rem',
        fontWeight: 700,
        lineHeight: '20px',
        textAlign: 'center',
        color: COLORS.orange,
        backgroundColor: `${COLORS.orange}18`,
      }}
    >
      {count}
    </Box>
  );
};

/**
 * Top-level switch between the three mapping screens. Each type keeps its own
 * URL (/vending/mappings/:type) so links and refresh land on the right tab.
 * Pass `refreshKey` that changes after a map/unmap so the counts follow.
 */
export const MappingTypeTabs = ({ active, refreshKey }: { active: MappingType; refreshKey?: unknown }) => {
  const navigate = useNavigate();
  const [counts, setCounts] = useState<Partial<Record<MappingType, number>>>({});

  useEffect(() => {
    let cancelled = false;
    loadPendingCounts()
      .then((next) => { if (!cancelled) setCounts(next); })
      .catch(() => { /* badges are optional — tabs still work without them */ });
    return () => { cancelled = true; };
  }, [refreshKey]);

  return (
    <Box sx={{ px: { xs: 2, md: 3 }, flexShrink: 0 }}>
      <Tabs
        value={active}
        onChange={(_, value: MappingType) => navigate(`/vending/mappings/${value}`)}
        variant="scrollable"
        scrollButtons={false}
        sx={{ ...mappingTabsSx, mb: 0 }}
      >
        {MAPPING_TYPES.map((type) => (
          <Tab
            key={type.value}
            value={type.value}
            label={(
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {type.label}
                <PendingBadge count={counts[type.value]} />
              </Box>
            )}
          />
        ))}
      </Tabs>
    </Box>
  );
};

/** Status filter inside a mapping type — chips, so it doesn't stack a second tab row. */
export function MappingStatusFilter<T extends string>({
  value,
  options,
  onChange,
}: {
  value: T;
  options: { value: T; label: string }[];
  onChange: (value: T) => void;
}) {
  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }} role="group" aria-label="Filter by status">
      {options.map((option) => {
        const selected = option.value === value;
        return (
          <Chip
            key={option.value}
            label={option.label}
            size="small"
            clickable
            onClick={() => onChange(option.value)}
            aria-pressed={selected}
            sx={{
              height: 30,
              fontWeight: 600,
              color: selected ? '#fff' : COLORS.lightGray,
              backgroundColor: selected ? COLORS.teal : 'transparent',
              border: `1px solid ${selected ? COLORS.teal : COLORS.lightGray}55`,
              '&:hover': { backgroundColor: selected ? COLORS.darkTeal : `${COLORS.teal}0a` },
              '& .MuiChip-label': { px: 1.5 },
            }}
          />
        );
      })}
    </Box>
  );
}
